import { Box, Grid, Typography, Button } from "@mui/material";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { LuCalculator, LuCoins, LuTable } from "react-icons/lu";
import SummarizeIcon from "@mui/icons-material/Summarize";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { Banner, WorkspaceHeader } from "../../components/m3";
import CircularityReportButton from "../../circularity-components/CircularityReportButton";
import CircularityKpiTiles from "../../circularity-components/CircularityKpiTiles";
import { useNavigate } from "react-router-dom";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import CircularChart from "../../circularity-components/CircularChart";
import CircularPCIChart from "../../circularity-components/CircularPCIChart";
import MaterialDetailCard from "../../circularity-components/MaterialDetailCard";
import NomarlizedGwpVsPercentageMciChart from "../../circularity-components/NomarlizedGwpVsPercentageMciChart";
import NomarlizedGwpVsPercentagePciChart from "../../circularity-components/NomarlizedGwpVsPercentagePciChart";
import { computePciData } from "../../circularity-components/computePciData";
import ProjectMCICalculationTable from "../../circularity-components/ProjectMCICalculationTable";
import SaveButton from "../../components/button/SaveButton";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import DesignAssessmentPanel from "./Circularity/DesignAssessmentPanel";
import ScenarioPanel from "./Circularity/ScenarioPanel";
import BenchmarkPanel from "./Circularity/BenchmarkPanel";
import CircularityReportPanel from "./Circularity/CircularityReportPanel";
import ScopeBoundaryPanel from "./Circularity/ScopeBoundaryPanel";
import CircularityAdminTabControls, { useCircularityAdminTabs } from "../../circularity-components/CircularityAdminTabControls";
import { useInvalidateProductCache } from "../../utilities/useProductQueries";
import { Circularity } from "../../db_access/circularity";
import { EnvFactor } from "../../db_access/env_factor";
import { Product } from "../../db_access/product";
import { calculate_combined_data, recalculate_row_mci } from "../../functions/CircularityFunctions";
import routing from "../../routing";
import "../../style/components-style/Table.css";
import "../../style/pages-style/Circularity.css";
import CircularityFlowChart from "./Circularity/CircularityFlowChart";
import { getDefaultDisassemblyFactors } from "./Circularity/pciOptions";
import MaterialCompositionTable from "./Circularity/MaterialCompositionTable";
import UtilityTable from "./Circularity/UtilityTable";
import config from "../../config";

/**
 * ProductCircularity Orchestrator
 * High-Reliability AI Trigger and Distinctive Metric Cards.
 */
const ProductCircularity = () => {
  const { t } = useTranslation();
  const {
    selectedProducts,
    refresh,
    setRefresh,
    setIsSaveVisible,
    setIsLoading,
    isSaveClicked,
    setIsSaveClicked,
    isDark,
    enqueueAlert,
    setLeftSidebarCollapse
  } = useContextProvider();

  const navigate = useNavigate();

  // TABS
  const scope_boundary = { label: "Boundary Settings", value: "Boundary Settings" };
  const overview = { label: "Overview", value: "Overview" };
  const utility = { label: "Utility", value: "Utility" };
  const mci_calculation = { label: "MCI Calculation", value: "MCI Calculation" };
  const analytics = { label: "Analytics", value: "Analytics" };
  const tabs = [overview, utility, mci_calculation, analytics];

  // ISO TABS
  const design_assessment = { label: "Design Assessment", value: "Design Assessment" };
  const scenario_what_if = { label: "Scenario/What-if", value: "Scenario/What-if" };
  const benchmarking = { label: "AI Recommendation", value: "AI Recommendation" };
  const pcds_report = { label: "Circularity Report", value: "Circularity Report", tabIcon: SummarizeIcon };

  // STATE
  const [materials, setMaterials] = useState([]);
  const [circularities, setCircularities] = useState([]);
  const [selectedEnvFactorID, setSelectedEnvFactorID] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState();
  const [activeTab, setActiveTab] = useState(overview.value);
  const [service_life, setService_life] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  const aiTriggeredForId = useRef(null);
  const [showInfoBar, setShowInfoBar] = useState(false);
  const [isNewProduct, setIsNewProduct] = useState(false);

  // Indicator scope toggles (Scope & Boundary) — shared with the Utility tab's sub-tabs
  const [scopeEnergy, setScopeEnergy] = useState(false);
  const [scopeWater, setScopeWater] = useState(false);
  const [scopeEconomic, setScopeEconomic] = useState(false);
  const [scopeDesign, setScopeDesign] = useState(false);
  const [scopeScenario, setScopeScenario] = useState(false);

  // ScopeBoundaryPanel lifted configurations
  const [start, setStart] = useState("2026-01-01");
  const [end, setEnd] = useState("2026-12-31");
  const [cutoff, setCutoff] = useState("Fiscal year-end");
  const [flow, setFlow] = useState("gate");
  const [materiality, setMateriality] = useState(95);

  const boundaryState = useMemo(() => ({
    start, end, cutoff, flow, materiality
  }), [start, end, cutoff, flow, materiality]);

  // ─── Persist Scope & Boundary toggles per product ──────────────────────────
  // Survives a page refresh or navigating away and back.
  const scopeTogglesKey = selectedProduct?.id ? `arivu_product_scope_toggles_${selectedProduct.id}` : null;
  const scopeTogglesLoaded = useRef(false);

  useEffect(() => {
    scopeTogglesLoaded.current = false;
    if (!scopeTogglesKey) return;
    try {
      const saved = localStorage.getItem(scopeTogglesKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        setScopeEnergy(!!parsed.scopeEnergy);
        setScopeWater(!!parsed.scopeWater);
        setScopeEconomic(!!parsed.scopeEconomic);
        setScopeDesign(!!parsed.scopeDesign);
        setScopeScenario(!!parsed.scopeScenario);
        if (parsed.start) setStart(parsed.start);
        if (parsed.end) setEnd(parsed.end);
        if (parsed.cutoff) setCutoff(parsed.cutoff);
        if (parsed.flow) setFlow(parsed.flow);
        if (typeof parsed.materiality === "number") setMateriality(parsed.materiality);
      } else {
        setScopeEnergy(false);
        setScopeWater(false);
        setScopeEconomic(false);
        setScopeDesign(false);
        setScopeScenario(false);
      }
    } catch (e) {
      console.error("Failed to load Scope & Boundary toggle preferences", e);
    } finally {
      scopeTogglesLoaded.current = true;
    }
  }, [scopeTogglesKey]);

  useEffect(() => {
    // Skip the write that would otherwise fire immediately after the load effect above
    // sets these same values back onto state (harmless, but avoids a redundant write).
    if (!scopeTogglesKey || !scopeTogglesLoaded.current) return;
    localStorage.setItem(
      scopeTogglesKey,
      JSON.stringify({
        scopeEnergy,
        scopeWater,
        scopeEconomic,
        scopeDesign,
        scopeScenario,
        start,
        end,
        cutoff,
        flow,
        materiality,
      })
    );
  }, [
    scopeTogglesKey,
    scopeEnergy,
    scopeWater,
    scopeEconomic,
    scopeDesign,
    scopeScenario,
    start,
    end,
    cutoff,
    flow,
    materiality,
  ]);

  const {
    isAdmin,
    adminTabsPrefs,
    updatePref,
    setAllPrefs,
    showUtilityTab,
    showMciCalcTab,
    showDesignAssessmentTab,
    showAiRecommendTab,
    activeAdminTabsCount,
  } = useCircularityAdminTabs();

  const isDesignAssessmentVisible = scopeDesign || showDesignAssessmentTab;

  // Auto-switch to overview when a hidden tab is currently active
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === design_assessment.value && !isDesignAssessmentVisible) setActiveTab(overview.value);
    if (activeTab === scenario_what_if.value && !scopeScenario) setActiveTab(overview.value);
    if (activeTab === utility.value && !showUtilityTab) setActiveTab(overview.value);
    if (activeTab === mci_calculation.value && !showMciCalcTab) setActiveTab(overview.value);
    if (activeTab === benchmarking.value && !showAiRecommendTab) setActiveTab(overview.value);
  }, [isDesignAssessmentVisible, scopeScenario, showUtilityTab, showMciCalcTab, showAiRecommendTab, activeTab]);

  useEffect(() => {
    if (activeTab === design_assessment.value) {
      setLeftSidebarCollapse(true);
    }
  }, [activeTab, setLeftSidebarCollapse]);

  // Ref for the silent MCI auto-sync debounce timer
  const mciSyncTimeoutRef = useRef(null);
  const { invalidateDashboard } = useInvalidateProductCache();

  // CALCULATIONS
  const [percentage_product_mci, setPercentage_product_mci] = useState(0);
  const [mci_sum, setMci_sum] = useState(0);
  const [mass_sum, setMass_sum] = useState(0);
  const [cost_sum, setCost_sum] = useState(0);
  const [gwp_total_sum, setGwp_total_sum] = useState(0);
  const [combined_circularities, setCombined_circularities] = useState([]);

  const combined_data = useMemo(() => ({
    mass_sum, cost_sum, gwp_total_sum, mci_sum, percentage_product_mci, combined_circularities, service_life
  }), [mass_sum, cost_sum, gwp_total_sum, mci_sum, percentage_product_mci, combined_circularities, service_life]);

  const pciData = useMemo(() => {
    return computePciData(combined_data, "Product", selectedProduct?.id);
  }, [combined_data, selectedProduct?.id]);

  const hasPci = useMemo(() => {
    return (pciData?.percentageOverallPci || 0) > 0;
  }, [pciData]);

  const selected_material = useMemo(() => {
    return combined_data.combined_circularities.find((m) => m.env_id === selectedEnvFactorID);
  }, [combined_data.combined_circularities, selectedEnvFactorID]);

  /**
   * Handle Smart Fill with AI
   */
  const handleSmartFill = useCallback(async (currentCircularities, currentMaterials) => {
    if (!currentCircularities?.length || !currentMaterials?.length || aiLoading) return;

    setAiLoading(true);
    aiTriggeredForId.current = selectedProduct?.id;

    try {
      const sortedMaterials = [...currentMaterials].sort((a, b) => String(a.id).localeCompare(String(b.id)));

      // Extract user context for AI token tracking
      let userContext = {};
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const u = JSON.parse(storedUser);
          userContext = { user_id: u.id || u.user_id, company_id: u.company_id };
        }
      } catch (e) {
        // ignore parse error
      }

      const response = await Circularity.ai_suggest({
        materials: sortedMaterials,
        product_context: {
          name: selectedProduct?.name || selectedProduct?.product_name,
          category: selectedProduct?.category || selectedProduct?.type,
          service_life: service_life,
        },
        ...userContext,
      });

      if (!response.ok) throw new Error("AI Suggestion request failed");
      const allSuggestions = await response.json();

        const updatedCircularities = currentCircularities.map((c) => {
        const materialIndex = sortedMaterials.findIndex(m => String(m.id) === String(c.env_id) || String(m.id) === String(c.id));
        const sug = materialIndex !== -1 ? allSuggestions[String(materialIndex)] : null;

        if (sug) {
          // Resolve im_recycled_content: prefer AI suggestion, fall back to existing value
          const aiRecycledContent = sug.im_recycled_content !== null && sug.im_recycled_content !== undefined
            ? Number(sug.im_recycled_content)
            : (c.im_recycled_content !== undefined ? Number(c.im_recycled_content) : 0);

          // im_virgin_content is always derived: 100 - recycled (system rule — never from AI directly)
          const derivedVirginContent = Math.max(0, Math.min(100, 100 - aiRecycledContent));

          // om_recycling: prefer AI suggestion, fall back to existing value
          const aiOmRecycling = sug.om_recycling !== null && sug.om_recycling !== undefined
            ? Number(sug.om_recycling)
            : (c.om_recycling !== undefined ? Number(c.om_recycling) : 80);

          // Industry average service life from AI
          const aiIndustryLife = sug.uf_industry_avg_life !== undefined
            ? Number(sug.uf_industry_avg_life)
            : (sug.rsl !== undefined ? Number(sug.rsl) : (Number(c.uf_industry_avg_life) || 20));

          // Product life: prefer product service_life if set > 0, otherwise AI industry avg, otherwise existing > 0, fallback 20
          const resolvedProductLife = Number(service_life) > 0
            ? Number(service_life)
            : (aiIndustryLife || (Number(c.uf_product_life) > 0 ? Number(c.uf_product_life) : 20));

          const updated = {
            ...c,
            // 1. Utility tab: Industry average service life
            uf_product_life: resolvedProductLife,
            uf_industry_avg_life: aiIndustryLife,
            // 2. End of Life tab: Recycling Efficiency
            eol_recycling_efficiency: sug.eol_recycling_efficiency !== undefined ? Number(sug.eol_recycling_efficiency) : (sug.efficiency !== undefined ? Number(sug.efficiency) : (c.eol_recycling_efficiency || 90)),
            eol_feedstock_efficiency: sug.eol_feedstock_efficiency !== undefined ? Number(sug.eol_feedstock_efficiency) : (c.eol_feedstock_efficiency || 90),
            // 3. Output Materials: Sent to Recycling
            om_recycling: aiOmRecycling,
            // 4. Input Materials: Recycled Content (AI-estimated) & Virgin Content (derived)
            im_recycled_content: aiRecycledContent,
            im_virgin_content: derivedVirginContent,
          };
          return recalculate_row_mci(updated);
        }
        return c;
      });

      setCircularities(updatedCircularities);
      setShowInfoBar(true);
      setAiLoading(false);
      setIsSaveVisible(true);

    } catch (error) {
      console.error("[AI] Error:", error);
      setAiLoading(false);
    }
  }, [selectedProduct, service_life, aiLoading, setIsSaveVisible]);

  // INITIAL LOAD
  useEffect(() => {
    if (!selectedProduct?.id) return;
    Product.get_by_id(selectedProduct.id)
      .then((res) => res.json())
      .then((data) => {
        if (data?.[0]) setService_life(data[0].service_life || 0);
        setIsSaveVisible(false);
        setIsLoading(false);
        setIsSaveClicked(false);
      });
  }, [selectedProduct?.id]);

  useEffect(() => {
    if (selectedProducts.length === 1) {
      setSelectedProduct(selectedProducts[0]);
    } else {
      navigate(routing.sector.products.dashboard);
    }
  }, [selectedProducts]);

  useEffect(() => {
    if (!selectedProduct?.id) return;
    EnvFactor.get_materials_by_product_id(selectedProduct.id)
      .then((response) => response.json())
      .then((data) => setMaterials(data));
  }, [selectedProduct?.id, refresh]);

  useEffect(() => {
    if (materials.length === 0) {
      setCircularities([]);
      return;
    }
    Circularity.get_by_env_factor_ids(materials.map((item) => item.id))
      .then((res) => res.json())
      .then((response) => {
        const data = response.data || [];
        setCircularities(data);
        setIsNewProduct(!!response.is_new);
      });
  }, [materials]);

  useEffect(() => {
    if (materials.length === 0) return;

    // IMPORTANT: env_factor IDs are UUIDs (strings). Number() on a UUID = NaN.
    // NaN === NaN is always false. Must use String() comparison.
    const t0 = materials.map((m) => {
      const c = circularities.find((circ) => String(circ.env_id) === String(m.id) || String(circ.id) === String(m.id));
      return {
        ...m,
        ...(c || {}),
        description: m.description || c?.description || "Unknown Material",
        ct_factor: c?.ct_factor != null ? c.ct_factor : (m.ct_factor ?? getDefaultDisassemblyFactors(c).ct),
        ca_factor: c?.ca_factor != null ? c.ca_factor : (m.ca_factor ?? getDefaultDisassemblyFactors(c).ca),
        indep_factor: c?.indep_factor != null ? c.indep_factor : (m.indep_factor ?? getDefaultDisassemblyFactors(c).indep),
        gpe_factor: c?.gpe_factor != null ? c.gpe_factor : (m.gpe_factor ?? getDefaultDisassemblyFactors(c).gpe),
      };
    });

    if (circularities.length > 0) {
      const calculated = calculate_combined_data(t0);
      setMass_sum(calculated.mass_sum);
      setCost_sum(calculated.cost_sum);
      setGwp_total_sum(calculated.gwp_total_sum);
      setMci_sum(calculated.mci_sum);
      setPercentage_product_mci(calculated.percentage_product_mci);
      setCombined_circularities(calculated.combined_circularities);
    } else {
      setCombined_circularities(t0);
    }
  }, [circularities, materials]);

  useEffect(() => {
    const alreadyTriggered = aiTriggeredForId.current === selectedProduct?.id;

    if (isNewProduct && !alreadyTriggered && !aiLoading && circularities.length > 0) {
      const timer = setTimeout(() => {
        handleSmartFill(circularities, materials);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [circularities, materials, isNewProduct, selectedProduct?.id, aiLoading, handleSmartFill]);

  // ─── Silent MCI Auto-Sync ────────────────────────────────────────────────
  // Persists the live-computed MCI to the DB (debounced 1.5 s) so the
  // Product Dashboard shows the correct value without a Save click.
  useEffect(() => {
    if (!selectedProduct?.id || !(percentage_product_mci > 0)) return;

    if (mciSyncTimeoutRef.current) clearTimeout(mciSyncTimeoutRef.current);

    mciSyncTimeoutRef.current = setTimeout(async () => {
      try {
        await Product.save_mci(selectedProduct.id, percentage_product_mci);
        console.log('[ProductCircularity] 🔄 Auto-synced MCI to DB:', (percentage_product_mci * 100).toFixed(2) + '%');
        invalidateDashboard();
      } catch (err) {
        console.error('[ProductCircularity] ❌ Auto-sync MCI failed:', err);
      }
    }, 1500);

    return () => {
      if (mciSyncTimeoutRef.current) clearTimeout(mciSyncTimeoutRef.current);
    };
  }, [percentage_product_mci, selectedProduct?.id]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isSaveClicked && selectedProduct) {
      setIsLoading(true);
      const fullPayload = circularities.map((c) => ({
        ...c,
        id: !isNaN(Number(c.id)) ? Number(c.id) : c.id,
        env_id: c.env_id || c.id,
        ct_factor: c.ct_factor != null ? Number(c.ct_factor) : getDefaultDisassemblyFactors(c).ct,
        ca_factor: c.ca_factor != null ? Number(c.ca_factor) : getDefaultDisassemblyFactors(c).ca,
        indep_factor: c.indep_factor != null ? Number(c.indep_factor) : getDefaultDisassemblyFactors(c).indep,
        gpe_factor: c.gpe_factor != null ? Number(c.gpe_factor) : getDefaultDisassemblyFactors(c).gpe,
      }));

      Promise.all([
        Circularity.update_utilities(fullPayload),
        Circularity.update_end_of_lifes_materials(fullPayload),
        Circularity.update_input_materials(fullPayload),
        Circularity.update_output_materials(fullPayload),
        Product.save_mci(selectedProduct.id, percentage_product_mci)
      ])
        .then(() => {
          setIsLoading(false);
          setIsSaveClicked(false);
          setIsSaveVisible(false);
          setRefresh(r => !r);
          setIsNewProduct(false);
          if (enqueueAlert) enqueueAlert({ message: "Data saved successfully", severity: "success" });
        })
        .catch(err => {
          console.error("Save Error:", err);
          setIsLoading(false);
          setIsSaveClicked(false);
          if (enqueueAlert) enqueueAlert({ message: "Save failed. Please check your network.", severity: "error" });
        });
    }
  }, [isSaveClicked, percentage_product_mci, selectedProduct, circularities]);


  const openReportTab = useCallback(() => setActiveTab(pcds_report.value), [pcds_report.value]);
  const htmlReportInputs = useMemo(
    () => ({ entity: selectedProduct, topic: "Product", combined_data, pciData, boundaryState, serviceLife: service_life }),
    [selectedProduct, combined_data, pciData, boundaryState, service_life]
  );

  const tok = getOrgTokens(isDark);

  return (
    <MainContainer>
      <Box sx={{ mb: tok.spacing.pageGap }}>
        <CircularityKpiTiles
          massKg={mass_sum}
          gwpKg={gwp_total_sum}
          mciPercent={percentage_product_mci * 100}
          pciPercent={pciData?.percentageOverallPci || 0}
          rows={combined_data.combined_circularities}
          topic="Product"
        />
      </Box>

      <Box sx={{ mb: tok.spacing.pageGap }}>
        <WorkspaceHeader
          tabs={[
            overview,
            ...(showUtilityTab ? [utility] : []),
            ...(showMciCalcTab ? [mci_calculation] : []),
            ...(isDesignAssessmentVisible ? [design_assessment] : []),
            analytics,
            ...(scopeScenario ? [scenario_what_if] : []),
            ...(showAiRecommendTab ? [benchmarking] : []),
          ].map((tab) => ({ value: tab.value, label: t(tab.label) }))}
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel={t("Circularity sections")}
          actions={
            <>
              {isAdmin && (
                <CircularityAdminTabControls
                  adminTabsPrefs={adminTabsPrefs}
                  updatePref={updatePref}
                  setAllPrefs={setAllPrefs}
                  activeAdminTabsCount={activeAdminTabsCount}
                  isDark={isDark}
                />
              )}
              <CircularityReportButton
                active={activeTab === pcds_report.value}
                onOpenReport={openReportTab}
                reportInputs={htmlReportInputs}
              />
            </>
          }
        />
      </Box>

      {activeTab === design_assessment.value && (
        <Box sx={{ mb: 3 }}>
          <DesignAssessmentPanel
            combined_data={combined_data}
            type="Product"
            id={selectedProduct?.id}
            setCircularities={setCircularities}
            circularities={circularities}
          />
        </Box>
      )}

      {activeTab === scenario_what_if.value && (
        <Box sx={{ mb: 3 }}>
          <ScenarioPanel />
        </Box>
      )}

      {showAiRecommendTab && activeTab === benchmarking.value && (
        <Box sx={{ mb: 3 }}>
          <BenchmarkPanel combined_data={combined_data} />
        </Box>
      )}

      {activeTab === pcds_report.value && (
        <Box sx={{ mb: 3 }}>
          <CircularityReportPanel
            scopeEnergy={scopeEnergy}
            scopeWater={scopeWater}
            scopeEconomic={scopeEconomic}
            reportTitle="Product Circularity"
            productName={selectedProduct?.name}
            productId={selectedProduct?.id}
            productSubCategory={selectedProduct?.sub_category}
            serviceLife={service_life}
            combined_data={combined_data}
            boundaryState={boundaryState}
          />
        </Box>
      )}

      {/* TABS CONTENT */}
      <Box sx={{ display: activeTab === overview.value ? "block" : "none" }}>
        <MaterialCompositionTable
          combined_data={combined_data}
          circularities={circularities}
          setCircularities={setCircularities}
          materials={materials}
          aiLoading={aiLoading}
          handleSmartFill={handleSmartFill}
          type="Product"
          id={selectedProduct?.id}
          activeTab={activeTab}
          tabValue={overview.value}
        />
      </Box>

      <Box sx={{ display: activeTab === utility.value ? "block" : "none" }}>
        <UtilityTable
          combined_data={combined_data}
          activeTab={activeTab}
          tabValue={utility.value}
          setCircularities={setCircularities}
          materials={materials}
          aiLoading={aiLoading}
          handleSmartFill={handleSmartFill}
          scopeEnergy={scopeEnergy}
          scopeWater={scopeWater}
          scopeEconomic={scopeEconomic}
        />
      </Box>

      <Box sx={{ display: activeTab === mci_calculation.value ? "block" : "none" }}>
        <ProjectMCICalculationTable combined_data={combined_data} topic="Product" />
      </Box>

      {activeTab === analytics.value && (
        <Box sx={{ display: "flex", flexWrap: "wrap", flexDirection: "column", gap: 3 }}>
          <Grid container spacing={3}>
            {hasPci ? (
              <>
                {/* Adjacent Row 1: Material Circularity Indicator & Product Circularity Indicator */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <CircularChart setSelectedEnvFactorID={setSelectedEnvFactorID} title="Material Circularity Indicator" combined_data={combined_data} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <CircularPCIChart
                    setSelectedEnvFactorID={setSelectedEnvFactorID}
                    title="Product Circularity Indicator"
                    topic="Product"
                    combined_data={combined_data}
                    productId={selectedProduct?.id}
                  />
                </Grid>

                {/* Adjacent Row 2: GWP vs MCI & GWP vs PCI */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <NomarlizedGwpVsPercentageMciChart topic="Product" combined_data={combined_data} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <NomarlizedGwpVsPercentagePciChart
                    topic="Product"
                    combined_data={combined_data}
                    productId={selectedProduct?.id}
                  />
                </Grid>
              </>
            ) : (
              <>
                {/* Default Layout: Material Circularity Indicator & GWP vs MCI side-by-side */}
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <CircularChart setSelectedEnvFactorID={setSelectedEnvFactorID} title="Material Circularity Indicator" combined_data={combined_data} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} sx={{ height: 520 }}>
                  <NomarlizedGwpVsPercentageMciChart topic="Product" combined_data={combined_data} />
                </Grid>
              </>
            )}

            {/* Input -> end-of-life material flows, one row per material */}
            <Grid size={{ xs: 12 }}>
              <CircularityFlowChart circularities={combined_data.combined_circularities} />
            </Grid>
          </Grid>
          {selectedEnvFactorID && (
            <Box sx={{ mt: 2 }}>
              <MaterialDetailCard material={selected_material} />
            </Box>
          )}
        </Box>
      )}

      {showInfoBar && (
        <Box sx={{ mt: tok.spacing.sectionGap }}>
          <Banner tone="success" icon={<AutoAwesomeOutlinedIcon />} onDismiss={() => setShowInfoBar(false)}>
            <strong>{t("Intelligent Estimation Applied")}</strong>{" "}
            {t("Recycling efficiency and product life parameters have been estimated for the identified materials ({{count}} items). Please review and Save.", { count: materials.length })}
          </Banner>
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        <SaveButton />
      </Box>
    </MainContainer>
  );
};

export default ProductCircularity;
